/**
 * The one place in this repo that talks to the API.
 *
 * Three conventions are encoded here rather than left to call sites, because each
 * one has already cost somebody a debugging session:
 *
 * - **Two headers, always.** `Authorization: Bearer <token>` is the identity, verified
 *   from the JWT server-side. `user-id` is *only* the rate limiter's per-user key —
 *   omit it and every teacher on a school's network shares one IP bucket.
 * - **Refresh once on 401, then sign out.** The backend rotates the refresh token on
 *   every use, so concurrent 401s must share a single in-flight refresh rather than
 *   racing and burning it.
 * - **Status codes are load-bearing.** 404 means "not found *or* not yours", and must
 *   never be surfaced as a permission problem — which ids exist inside other schools
 *   is exactly what that indistinguishability hides.
 */

import type { SessionStore, StoredSession } from './session'
import { localStorageSession } from './session'

/** Raised for every non-2xx response. `status` is what callers branch on. */
export class ApiError extends Error {
  readonly status: number

  constructor(status: number, detail: string) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
  }

  /** Does not exist, or belongs to someone else — indistinguishable by design.
   *  Render as "not found". Never as "no permission". */
  get isNotFound(): boolean {
    return this.status === 404
  }

  /** Authenticated, in the right school, but the role is too low. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** A dependency is not switched on yet (billing, most often). Not an outage,
   *  and not the student's problem — say "not available yet", not "error". */
  get isUnavailable(): boolean {
    return this.status === 503
  }

  /** Rate limited. Backing off is the only correct response. */
  get isRateLimited(): boolean {
    return this.status === 429
  }
}

/** Signalled when the session cannot be renewed. Apps sign out on this. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Your session has expired. Please sign in again.')
    this.name = 'SessionExpiredError'
  }
}

export interface ClientConfig {
  /** Defaults to `/api/v1` — same-origin, served by the BFF worker. */
  baseUrl?: string
  store?: SessionStore
}

export interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** Extra headers. Cannot override `Authorization` or `user-id`. */
  headers?: Record<string, string>
  /** Query parameters. `undefined` and `null` values are dropped, not sent empty. */
  query?: Record<string, string | number | boolean | undefined | null>
}

export class ApiClient {
  readonly baseUrl: string
  readonly store: SessionStore

  private refreshInFlight: Promise<string | null> | null = null
  private sessionLostHandler: (() => void) | null = null

  constructor(config: ClientConfig = {}) {
    this.baseUrl = config.baseUrl ?? '/api/v1'
    this.store = config.store ?? localStorageSession()
  }

  // -- session ---------------------------------------------------------------

  get session(): StoredSession | null {
    return this.store.load()
  }

  get isSignedIn(): boolean {
    return this.store.load() !== null
  }

  /** Lets the app tear down its own state when a refresh finally fails. */
  onSessionLost(handler: (() => void) | null): void {
    this.sessionLostHandler = handler
  }

  private loseSession(): SessionExpiredError {
    this.store.clear()
    this.sessionLostHandler?.()
    return new SessionExpiredError()
  }

  // -- transport -------------------------------------------------------------

  url(path: string, query?: RequestOptions['query']): string {
    const base = `${this.baseUrl}${path}`
    if (!query) return base

    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue
      params.set(key, String(value))
    }
    const qs = params.toString()
    return qs ? `${base}?${qs}` : base
  }

  /** Headers for an authenticated request. Exported for the SSE helper, which
   *  has to build its own `fetch` to keep the response body streaming. */
  authHeaders(token: string, extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = { ...extra, Authorization: `Bearer ${token}` }
    const userId = this.store.load()?.userId
    if (userId) headers['user-id'] = userId
    return headers
  }

  private send(path: string, options: RequestOptions, token: string): Promise<Response> {
    const headers = this.authHeaders(token, options.headers)
    if (options.body !== undefined) headers['Content-Type'] = 'application/json'

    return fetch(this.url(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal ?? null,
    })
  }

  /**
   * Renews the access token. Returns null when renewal is impossible, which the
   * caller must treat as a terminal sign-out rather than a retryable failure.
   */
  private async refreshAccessToken(): Promise<string | null> {
    const stored = this.store.load()
    if (!stored?.refreshToken) return null

    let response: Response
    try {
      response = await fetch(this.url('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: stored.refreshToken }),
      })
    } catch {
      // Offline. Not proof the token is dead — but this request cannot proceed.
      return null
    }
    if (!response.ok) return null

    // Auth responses are snake_case: they predate the school API and are shared
    // with iOS. Everything under /teacher and /schools is camelCase.
    const body = (await response.json()) as {
      access_token: string
      refresh_token?: string | null
      user_id?: string
      expires_at?: number | null
    }

    this.store.save({
      accessToken: body.access_token,
      // The backend rotates it, but an omitted key means "keep the old one" —
      // overwriting with null here would sign the user out on the next 401.
      refreshToken: body.refresh_token ?? stored.refreshToken,
      userId: body.user_id ?? stored.userId,
      ...(body.expires_at ? { expiresAt: body.expires_at } : {}),
    })
    return body.access_token
  }

  /** Concurrent 401s must not each spend the rotating refresh token. */
  private refreshOnce(): Promise<string | null> {
    this.refreshInFlight ??= this.refreshAccessToken().finally(() => {
      this.refreshInFlight = null
    })
    return this.refreshInFlight
  }

  /** A response, authenticated and refreshed once — before any body is read.
   *  This is what SSE needs; `request` wraps it for ordinary JSON. */
  async fetchAuthed(path: string, options: RequestOptions = {}): Promise<Response> {
    const session = this.store.load()
    if (!session) throw new SessionExpiredError()

    let response = await this.send(path, options, session.accessToken)

    if (response.status === 401) {
      const renewed = await this.refreshOnce()
      if (!renewed) throw this.loseSession()

      response = await this.send(path, options, renewed)
      if (response.status === 401) throw this.loseSession()
    }

    return response
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.fetchAuthed(path, options)

    if (!response.ok) throw await errorFrom(response)
    if (response.status === 204) return undefined as T

    // 200 with an empty body happens on a few delete routes. `.json()` would throw.
    const text = await response.text()
    return (text ? JSON.parse(text) : undefined) as T
  }

  get<T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { query, ...(signal ? { signal } : {}) })
  }

  post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body })
  }

  put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body })
  }

  patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body })
  }

  delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' })
  }
}

/**
 * Turns a failed response into an `ApiError`.
 *
 * FastAPI's `detail` is a human-readable string for the errors we raise ourselves,
 * and an array of validation objects for a 422. The array is never shown: it names
 * internal field paths, and the redacting 422 handler exists precisely so those do
 * not leak.
 */
export async function errorFrom(response: Response): Promise<ApiError> {
  let detail = `Request failed (${response.status})`
  try {
    const body = await response.json()
    if (typeof body?.detail === 'string') detail = body.detail
    else if (Array.isArray(body?.detail)) detail = 'The request was rejected as invalid.'
  } catch {
    /* a body-less error is still an error */
  }
  return new ApiError(response.status, detail)
}
